/*
 * Copyright (c) 2022. Nedaware SL
 */

/**
 * The Uri class represents a URI in the system. The URI allow to identify entities
 * uniquely.
 *
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
import {Resource} from "./internal";
import {Type} from "@angular/core";
import {ApiInterface} from "./api-interface";

export class Uri {
    public static readonly SPECIALS = [
        // order matters for these
        '-', '[', ']',
        // order doesn't matter for any of these
        '/', '{', '}', '(', ')', '*', '+', '?', '.', '\\', '^', '$', '|'
    ];
    public static readonly REGEX = RegExp('[' + Uri.SPECIALS.join('\\') + ']', 'g');

    public static sEscapeRegExp(str) {
        return str.replace(Uri.REGEX, '\\$&');
    }

    public static sAreTheSame(a?: Uri | null, b?: Uri | null): boolean {
        if (a != null) {
            return a.isTheSame(b);
        } else {
            return b == null;
        }
    }

    public isTheSame(obj: any): boolean {
        if (!(obj instanceof Uri)) {
            return false;
        }
        return obj.toString() === this.toString();
    }

    public constructor(public readonly id: string[],
                       public readonly pattern: string,
                       public readonly model: Type<Resource>) {
    }

    public static uriMatchsPattern(uri: string, pattern: string): boolean {
        const regExp = Uri.searchRegexFromPattern(pattern);
        return regExp.test(uri);
    }

    public static fromUriStr(uri: string, pattern: string, model: Type<Resource>): Uri {
        const regExp = Uri.searchRegexFromPattern(pattern);
        const matches = regExp.exec(uri);
        if (!matches) {
            throw new Error('The given URI does not match the given pattern:\n' +
                'Uri: ' + uri + '\nPattern: ' + pattern + '\nGenerated regexp: ' + regExp.source);
        }
        const idValues = matches.filter((val, idx) => idx > 0);
        return new Uri(idValues, pattern, model);
    }

    /**
     * Dado un pattern de URI devuelve la expresión regular para buscar los valores de id
     */
    private static searchRegexFromPattern(pattern: string): RegExp {
        let strIdx = 0;
        let id: RegExpExecArray;
        let regExpStr = '^';

        const idSearch = /:[0-9]+/g;
        while ((id = idSearch.exec(pattern)) !== null) {
            const prev = Uri.sEscapeRegExp(pattern.substring(strIdx, id.index));
            strIdx = id.index + id[0].length;
            if (strIdx < pattern.length) {
                const next = Uri.sEscapeRegExp(pattern[strIdx]);
                regExpStr += `${prev}([^${next}]+)`;
            } else {
                regExpStr += `${prev}(.+)$`;
            }
        }
        if (strIdx < pattern.length) {
            const next = Uri.sEscapeRegExp(pattern.substring(strIdx));
            regExpStr += next + '$';
        }

        return new RegExp(regExpStr);
    }

    public clone(): Uri {
        return new Uri(this.id, this.pattern, this.model);
    }

    public toUrl(): URL {
        return new URL(this.toString(), ApiInterface.URL);
    }

    public toString(): string {
        return this.pattern.replace(/:([0-9]+)/g, (x, idx) => this.id[parseInt(idx, 10)]);
    }
}

export class TempUri extends Uri {
    private static _sNextId = 0;

    private static _sNextFakeId(): string[] {
        return [(this._sNextId++).toString(10)];
    }

    private constructor(id: string[], pattern: string, model: Type<Resource>) {
        super(id, pattern, model);
    }

    public static sNewTemp(model: Type<Resource>): TempUri {
        return new TempUri(TempUri._sNextFakeId(), '?temp:0', model);
    }

    public clone(): Uri {
        return new TempUri(this.id, this.pattern, this.model);
    }
}
